import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Descriptions,
  Tag,
  Progress,
  Button,
  Row,
  Col,
  Statistic,
  Spin,
  message,
  Modal,
  Typography,
  Space,
  Popconfirm,
} from 'antd';
import {
  ArrowLeftOutlined,
  LockOutlined,
  UnlockOutlined,
  DeleteOutlined,
  FileTextOutlined,
  SearchOutlined,
  MailOutlined,
  FilePdfOutlined,
  BookOutlined,
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { adminApi } from '../../services/admin';
import type { TenantDetail as TenantDetailType, HealthScore } from '../../types/admin';
import { COLORS } from '../../constants/colors';

const { Title, Text } = Typography;

// 状态标签颜色和文字
const statusConfig: Record<string, { color: string; text: string }> = {
  trial: { color: 'blue', text: '试用中' },
  active: { color: 'green', text: '活跃' },
  frozen: { color: 'orange', text: '已冻结' },
  cancelled: { color: 'red', text: '已注销' },
};

// 版本标签颜色
const planColors: Record<string, string> = {
  Free: 'default',
  Starter: 'blue',
  Professional: 'purple',
  Enterprise: 'gold',
};

// 版本中文名
const planNames: Record<string, string> = {
  Free: '免费版',
  Starter: '入门版',
  Professional: '专业版',
  Enterprise: '企业版',
};

// 风险等级标签
const riskLevelConfig: Record<string, { color: string; text: string }> = {
  healthy: { color: 'success', text: '健康' },
  warning: { color: 'warning', text: '警告' },
  critical: { color: 'error', text: '危险' },
};

export const TenantDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [tenant, setTenant] = useState<TenantDetailType | null>(null);
  const [health, setHealth] = useState<HealthScore | null>(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);

  const fetchData = useCallback(async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      const tenantId = parseInt(id, 10);
      const [tenantRes, healthRes] = await Promise.all([
        adminApi.getAdminTenantDetail(tenantId),
        adminApi.getAdminTenantHealth(tenantId),
      ]);

      if (tenantRes.data) {
        setTenant(tenantRes.data);
      }
      if (healthRes.data) {
        setHealth(healthRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch tenant detail:', error);
      message.error('获取租户详情失败');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 返回列表
  const handleBack = () => {
    navigate('/admin/tenants');
  };

  // 冻结租户
  const handleFreeze = async () => {
    if (!id) return;
    try {
      await adminApi.freezeTenant(parseInt(id, 10), { reason: '管理员操作冻结' });
      message.success('租户已冻结');
      fetchData();
    } catch (error) {
      console.error('Failed to freeze tenant:', error);
      message.error('冻结租户失败');
    }
  };

  // 解冻租户
  const handleUnfreeze = async () => {
    if (!id) return;
    try {
      await adminApi.unfreezeTenant(parseInt(id, 10));
      message.success('租户已解冻');
      fetchData();
    } catch (error) {
      console.error('Failed to unfreeze tenant:', error);
      message.error('解冻租户失败');
    }
  };

  // 注销租户
  const handleDelete = async () => {
    if (!id) return;
    try {
      await adminApi.deleteTenant(parseInt(id, 10));
      message.success('租户已注销');
      setDeleteModalVisible(false);
      navigate('/admin/tenants');
    } catch (error) {
      console.error('Failed to delete tenant:', error);
      message.error('注销租户失败');
    }
  };

  // 获取健康度颜色
  const getHealthColor = (score: number) => {
    if (score < 40) return COLORS.error;
    if (score < 70) return COLORS.warning;
    return COLORS.success;
  };

  if (!tenant) {
    return (
      <Spin spinning={loading}>
        <div style={{ padding: 24 }}>
          <EmptyState onBack={handleBack} />
        </div>
      </Spin>
    );
  }

  const status = statusConfig[tenant.status] || { color: 'default', text: tenant.status };
  const riskLevel = health
    ? riskLevelConfig[health.risk_level] || { color: 'default', text: health.risk_level }
    : null;

  return (
    <Spin spinning={loading}>
      <div>
        {/* 页面标题和操作按钮 */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 24,
          }}
        >
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
              返回列表
            </Button>
            <Title level={4} style={{ margin: 0 }}>
              租户详情
            </Title>
          </Space>
          <Space>
            {tenant.status === 'frozen' ? (
              <Button
                type="primary"
                icon={<UnlockOutlined />}
                onClick={handleUnfreeze}
              >
                解冻租户
              </Button>
            ) : (
              <Popconfirm
                title="确认冻结"
                description="确定要冻结该租户吗？冻结后租户将无法使用平台功能。"
                onConfirm={handleFreeze}
                okText="确认"
                cancelText="取消"
              >
                <Button icon={<LockOutlined />}>冻结租户</Button>
              </Popconfirm>
            )}
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={() => setDeleteModalVisible(true)}
            >
              注销租户
            </Button>
          </Space>
        </div>

        {/* 基本信息 */}
        <Card title="基本信息" style={{ marginBottom: 24 }}>
          <Descriptions bordered column={3}>
            <Descriptions.Item label="租户名称">
              <Text strong style={{ fontSize: 16 }}>
                {tenant.name}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color={status.color}>{status.text}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="订阅版本">
              <Tag color={planColors[tenant.plan_name] || 'default'}>
                {planNames[tenant.plan_name] || tenant.plan_name}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="订阅状态">
              {tenant.subscription_status}
            </Descriptions.Item>
            <Descriptions.Item label="成员数">{tenant.member_count} 人</Descriptions.Item>
            <Descriptions.Item label="健康度评分">
              <Progress
                percent={tenant.health_score}
                size="small"
                strokeColor={getHealthColor(tenant.health_score)}
                style={{ width: 120 }}
              />
            </Descriptions.Item>
            <Descriptions.Item label="创建时间">
              {tenant.created_at
                ? new Date(tenant.created_at).toLocaleString()
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="最后活跃">
              {tenant.last_active_at
                ? new Date(tenant.last_active_at).toLocaleString()
                : '-'}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* 健康度评分 */}
        {health && (
          <Card title="健康度评分" style={{ marginBottom: 24 }}>
            <Row gutter={[48, 24]}>
              <Col span={6} style={{ textAlign: 'center' }}>
                <div
                  style={{
                    fontSize: 48,
                    fontWeight: 700,
                    color: getHealthColor(health.total_score),
                    lineHeight: 1,
                  }}
                >
                  {health.total_score}
                </div>
                <div style={{ marginTop: 8 }}>
                  <Tag color={riskLevel?.color}>{riskLevel?.text}</Tag>
                </div>
                <Text type="secondary">综合评分</Text>
              </Col>
              <Col span={18}>
                <Row gutter={[24, 16]}>
                  <Col span={12}>
                    <div style={{ marginBottom: 8 }}>
                      <Text>活跃度</Text>
                      <Text type="secondary" style={{ float: 'right' }}>
                        {health.activity_score}分
                      </Text>
                    </div>
                    <Progress
                      percent={health.activity_score}
                      strokeColor={getHealthColor(health.activity_score)}
                      showInfo={false}
                    />
                  </Col>
                  <Col span={12}>
                    <div style={{ marginBottom: 8 }}>
                      <Text>功能使用广度</Text>
                      <Text type="secondary" style={{ float: 'right' }}>
                        {health.feature_breadth_score}分
                      </Text>
                    </div>
                    <Progress
                      percent={health.feature_breadth_score}
                      strokeColor={getHealthColor(health.feature_breadth_score)}
                      showInfo={false}
                    />
                  </Col>
                  <Col span={12}>
                    <div style={{ marginBottom: 8 }}>
                      <Text>知识库增长</Text>
                      <Text type="secondary" style={{ float: 'right' }}>
                        {health.knowledge_growth_score}分
                      </Text>
                    </div>
                    <Progress
                      percent={health.knowledge_growth_score}
                      strokeColor={getHealthColor(health.knowledge_growth_score)}
                      showInfo={false}
                    />
                  </Col>
                  <Col span={12}>
                    <div style={{ marginBottom: 8 }}>
                      <Text>付费意愿</Text>
                      <Text type="secondary" style={{ float: 'right' }}>
                        {health.payment_willingness_score}分
                      </Text>
                    </div>
                    <Progress
                      percent={health.payment_willingness_score}
                      strokeColor={getHealthColor(health.payment_willingness_score)}
                      showInfo={false}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          </Card>
        )}

        {/* 用量统计 */}
        <Card title="用量统计">
          <Row gutter={[24, 24]}>
            <Col span={4}>
              <Statistic
                title="提案数"
                value={tenant.usage_stats?.proposals || 0}
                prefix={<FileTextOutlined style={{ color: COLORS.primary }} />}
              />
            </Col>
            <Col span={4}>
              <Statistic
                title="搜索次数"
                value={tenant.usage_stats?.searches || 0}
                prefix={<SearchOutlined style={{ color: COLORS.success }} />}
              />
            </Col>
            <Col span={4}>
              <Statistic
                title="邮件发送"
                value={tenant.usage_stats?.emails || 0}
                prefix={<MailOutlined style={{ color: COLORS.warning }} />}
              />
            </Col>
            <Col span={4}>
              <Statistic
                title="文档生成"
                value={tenant.usage_stats?.documents || 0}
                prefix={<FilePdfOutlined style={{ color: COLORS.magenta }} />}
              />
            </Col>
            <Col span={4}>
              <Statistic
                title="知识条目"
                value={tenant.usage_stats?.knowledge_entries || 0}
                prefix={<BookOutlined style={{ color: COLORS.purple }} />}
              />
            </Col>
          </Row>
        </Card>

        {/* 注销确认弹窗 */}
        <Modal
          title="确认注销租户"
          open={deleteModalVisible}
          onOk={handleDelete}
          onCancel={() => setDeleteModalVisible(false)}
          okText="确认注销"
          cancelText="取消"
          okButtonProps={{ danger: true }}
        >
          <p>您确定要注销租户 <Text strong>{tenant.name}</Text> 吗？</p>
          <p style={{ color: COLORS.error }}>
            此操作不可恢复，注销后该租户的所有数据将被删除！
          </p>
        </Modal>
      </div>
    </Spin>
  );
};

// 空状态组件
const EmptyState: React.FC<{ onBack: () => void }> = ({ onBack }) => (
  <div style={{ textAlign: 'center', padding: '40px 0' }}>
    <Text type="secondary">租户不存在或已被删除</Text>
    <div style={{ marginTop: 16 }}>
      <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
        返回列表
      </Button>
    </div>
  </div>
);

export default TenantDetail;
